import React, { useRef, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { toast } from "react-toastify";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useAppMeta } from "hooks";
import {
    useLazySearchLeadsQuery,
    useAssignResponsibleMutation,
} from "store/services/vendorLeadApi";
import { useLazySearchUsersQuery } from "store/services/userApi";

const breadcrumbs = [
    { title: "Leads", path: "/app/crm/vendor-lead/list" },
    { title: "Assign Responsible" },
];

const getLeadLabel = (lead) => {
    const name =
        lead?.fullName || lead?.companyName || lead?.contactPersonName || "";
    return `${lead?.leadId ?? ""} — ${name || lead?.email || lead?.phone || ""}`.trim();
};

const AssignLeadScreen = () => {
    useAppMeta("Assign Responsible");

    const [selectedLeads, setSelectedLeads] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [leadOptions, setLeadOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);

    const leadTimerRef = useRef(null);
    const userTimerRef = useRef(null);

    const [searchLeads, { isLoading: searchingLeads }] =
        useLazySearchLeadsQuery();
    const [searchUsers, { isLoading: searchingUsers }] =
        useLazySearchUsersQuery();
    const [assignResponsible, { isLoading: assigning }] =
        useAssignResponsibleMutation();

    const handleLeadSearch = (_, val) => {
        clearTimeout(leadTimerRef.current);
        leadTimerRef.current = setTimeout(async () => {
            if (!val) return;
            try {
                const res = await searchLeads({ q: val }).unwrap();
                setLeadOptions(res || []);
            } catch (_) { }
        }, 300);
    };

    const handleUserSearch = (_, val) => {
        clearTimeout(userTimerRef.current);
        userTimerRef.current = setTimeout(async () => {
            if (!val) return;
            try {
                const res = await searchUsers({ q: val }).unwrap();
                setUserOptions(res || []);
            } catch (_) { }
        }, 300);
    };

    const handleAssign = async () => {
        if (!selectedLeads.length) {
            toast.warn("Please select at least one lead");
            return;
        }
        if (!selectedUser) {
            toast.warn("Please select a user to assign");
            return;
        }

        try {
            const res = await assignResponsible({
                leadIds: selectedLeads.map((l) => l._id),
                responsible: selectedUser._id,
            }).unwrap();

            toast.success(res?.message ?? "Leads assigned successfully");

            // Reset
            setSelectedLeads([]);
            setSelectedUser(null);
            setLeadOptions([]);
            setUserOptions([]);
        } catch (err) {
            toast.error(err?.data?.message ?? "Could not assign leads");
        }
    };

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} isBusy={assigning}>
            <BreadcrumbLayout.Paper>
                <Box padding={3}>
                    <Grid container spacing={4}>

                        {/* ── Select Leads ── */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary" mb={1}>
                                Select Leads
                            </Typography>
                            <BaseAutocomplete
                                multiple
                                fullWidth
                                label="Search by name, email, phone or lead ID"
                                value={selectedLeads}
                                onChange={(_, data) => setSelectedLeads(data)}
                                onInputChange={handleLeadSearch}
                                getOptionLabel={(option) => getLeadLabel(option)}
                                isOptionEqualToValue={(option, value) =>
                                    option?._id === value?._id
                                }
                                options={leadOptions}
                                loading={searchingLeads}
                                filterOptions={(x) => x}
                            />
                        </Grid>

                        {/* ── Select User ── */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary" mb={1}>
                                Assign To
                            </Typography>
                            <BaseAutocomplete
                                fullWidth
                                label="Search user by name"
                                value={selectedUser ?? null}
                                onChange={(_, data) => setSelectedUser(data)}
                                onInputChange={handleUserSearch}
                                getOptionLabel={(option) => option?.name ?? ""}
                                isOptionEqualToValue={(option, value) =>
                                    option?._id === value?._id
                                }
                                options={userOptions}
                                loading={searchingUsers}
                                filterOptions={(x) => x}
                            />
                        </Grid>

                        {/* ── Summary ── */}
                        {(selectedLeads.length > 0 || selectedUser) && (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    {selectedLeads.length > 0 && (
                                        <>
                                            <strong>{selectedLeads.length}</strong> lead(s) selected
                                        </>
                                    )}
                                    {selectedLeads.length > 0 && selectedUser && " → "}
                                    {selectedUser && (
                                        <>
                                            assigning to <strong>{selectedUser.name}</strong>
                                        </>
                                    )}
                                </Typography>
                            </Grid>
                        )}

                        {/* ── Action ── */}
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                onClick={handleAssign}
                                disabled={assigning || !selectedLeads.length || !selectedUser}
                            >
                                {assigning ? "Assigning..." : "Assign"}
                            </Button>
                        </Grid>

                    </Grid>
                </Box>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default AssignLeadScreen;